import React, { useState, useEffect } from 'react';
import Icon from './AppIcon';
import { supabase } from '../lib/supabase';
import QRCode from 'qrcode'; 
import { uploadBadge } from '../lib/services/documentServices';
import { useTranslation } from 'react-i18next'; // Dodano

const QrCodeModal = ({ isOpen, onClose, data }) => {
  const { t } = useTranslation(); // Inicijalizacija
  const [badgeLoading, setBadgeLoading] = useState(false);
  const [badgeUrl, setBadgeUrl] = useState(null);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    if (data && isOpen) {
      loadBadgeImage();
    }
    return () => cleanupUrl();
  }, [data, isOpen]);

  const cleanupUrl = () => {
    if (badgeUrl) {
      window.URL.revokeObjectURL(badgeUrl);
      setBadgeUrl(null);
    }
  };

  const loadBadgeImage = async () => {
    cleanupUrl();
    setIsFetching(true);
    
    try {
      const path = `${data.id_azienda}/${data.id}/badge/qrcode.png`;
      const { data: blob, error } = await supabase.storage
        .from('aziende')
        .download(path);

      if (error) {
        setBadgeUrl(null);
        return;
      }

      const url = window.URL.createObjectURL(blob);
      setBadgeUrl(url);
    } catch (err) {
      console.error("Errore fetch:", err.message);
    } finally {
      setIsFetching(false);
    }
  };

  const handleCreateBadge = async () => {
    const isRegenerate = !!badgeUrl;
    // Prevedeni confirm dijalozi
    const confirmMsg = isRegenerate ? t('qrAdmin.confirmOverwrite') : t('qrAdmin.confirmNew');
    if (!window.confirm(confirmMsg)) return;
    
    setBadgeLoading(true);
    try {
      const payload = JSON.stringify({
        a: data.id_azienda,
        u: data.id
      });

      const qrDataUrl = await QRCode.toDataURL(payload, {
        width: 600,
        margin: 2,
        color: { dark: '#000000', light: '#ffffff' }
      });

      const res = await fetch(qrDataUrl);
      const blob = await res.blob();

      const uploadRes = await uploadBadge({
        fileBlob: blob,
        idUtente: data.id,
        idAzienda: data.id_azienda
      });

      if (uploadRes.success) {
        alert(t('qrAdmin.success'));
        await loadBadgeImage();
      }
    } catch (err) {
      alert(t('qrAdmin.securityError'));
      console.error(err);
    } finally {
      setBadgeLoading(false);
    }
  };

  if (!isOpen || !data) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-card w-full max-w-sm rounded-3xl border border-border shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
        
        <div className="p-6 border-b border-border flex justify-between items-center bg-muted/20">
          <div className="flex items-center gap-2">
            <Icon name="QrCode" size={20} className="text-primary" />
            <h3 className="font-bold text-lg uppercase tracking-tighter">{t('qrAdmin.title')}</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-full transition-colors"><Icon name="X" size={20} /></button>
        </div>

        <div className="p-8 flex flex-col items-center">
          <div className="w-full bg-muted/30 p-6 rounded-3xl border border-dashed border-border flex flex-col items-center justify-center min-h-[250px]">
            
            {isFetching ? (
              <div className="flex flex-col items-center gap-2">
                <div className="animate-spin text-primary"><Icon name="Loader2" size={40} /></div>
                <p className="text-[10px] font-bold text-muted-foreground uppercase">{t('qrAdmin.sync')}</p>
              </div>
            ) : badgeUrl ? (
              <div className="space-y-6 text-center animate-in fade-in">
                <div className="bg-white p-3 rounded-2xl shadow-lg border border-border">
                  <img src={badgeUrl} alt="QR Badge" className="w-48 h-48" />
                </div>
                <p className="text-[10px] font-black uppercase text-muted-foreground tracking-widest">
                  {data.nome} {data.cognome}
                </p>
              </div>
            ) : (
              <div className="text-center space-y-4">
                <div className="opacity-10"><Icon name="QrCode" size={64} /></div>
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest">{t('qrAdmin.notFound')}</p>
              </div>
            )}
          </div>
        </div>

        <div className="p-6 bg-muted/10 border-t border-border flex flex-col gap-3">
          {badgeUrl ? (
            <>
              <a 
                href={badgeUrl} 
                download={`Badge_${data.cognome}.png`}
                className="w-full bg-primary text-white py-4 rounded-2xl font-bold flex items-center justify-center gap-2 hover:opacity-90 transition-all shadow-lg shadow-primary/20"
              >
                <Icon name="Download" size={18} /> {t('qrAdmin.download')}
              </a>
              <button 
                onClick={handleCreateBadge}
                disabled={badgeLoading}
                className="text-[10px] font-black text-muted-foreground uppercase hover:text-primary transition-colors flex items-center justify-center gap-2 mt-2"
              >
                {badgeLoading ? <Icon name="Loader2" size={12} className="animate-spin" /> : <Icon name="RefreshCw" size={12} />}
                {t('qrAdmin.regenerate')}
              </button>
            </>
          ) : (
            <button 
              onClick={handleCreateBadge}
              disabled={badgeLoading}
              className="w-full bg-foreground text-background py-4 rounded-2xl font-bold flex items-center justify-center gap-2 hover:opacity-90 transition-all disabled:opacity-50"
            >
              {badgeLoading ? <Icon name="Loader2" size={18} className="animate-spin" /> : <Icon name="Plus" size={18} />}
              {t('qrAdmin.generateNow')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QrCodeModal;